import { Fragment, useState, useEffect, useRef, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import {
    Users,
    ExternalLink,
    Download,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Search,
    X
} from "lucide-react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import toast from "react-hot-toast";

import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";
import PageWrapper from "../../components/page-wrapper/page-wrapper.components";
import PaViewLink from "../../components/ui/PaViewLink";
import ScrollX from "../../components/ui/ScrollX";
import ClientSummaryPanel from "../../components/client-summary/client-summary-panel";

const CompaniesPage = ({ currentUser, onSessionExpired, onTokenRefresh }) => {
    const [userInfo, setUserInfo] = useState({});
    const [loadingError, setLoadingError] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const toastIdRef = useRef(null);
    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const fetchData = async () => {
        toastIdRef.current = toast.loading('Loading clients...');
        try {
            const response = await authFetch(
                `${apiUrl}/advisors?userId=${currentUser.id}`,
                {},
                authParams
            );
            if (response.ok) {
                const data = await response.json();
                toast.dismiss(toastIdRef.current);
                setUserInfo(data);
            } else {
                toast.error('Failed to load clients', { id: toastIdRef.current });
            }
        } catch (err) {
            toast.dismiss(toastIdRef.current);
            if (!(err instanceof SessionExpiredError)) {
                setLoadingError(true);
            }
        }
    };

    useEffect(() => {
        fetchData();
        return () => {
            if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const companies = useMemo(() => userInfo.companies || [], [userInfo.companies]);
    const totalEmployees = useMemo(() => companies.reduce((sum, c) => sum + (c.activeEmployees || 0), 0), [companies]);

    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return companies;
        const q = searchQuery.toLowerCase();
        return companies.filter((c) => c.name?.toLowerCase().includes(q));
    }, [companies, searchQuery]);

    const columns = useMemo(() => [
        {
            Header: 'Company (Policy)',
            accessor: 'name',
            Cell: ({ row }) => (
                row.original.isPlanAdmin ? (
                    <PaViewLink
                        policyId={row.original.id}
                        className="text-primary hover:text-primary-dark font-medium inline-flex items-center gap-1 transition-colors"
                    >
                        {row.original.name}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </PaViewLink>
                ) : (
                    <span className="font-medium text-gray-900 inline-flex items-center gap-2">
                        {row.original.name}
                        <span className="text-sm text-gray-400 font-normal">No Plan Admin Access</span>
                    </span>
                )
            ),
        },
        {
            Header: 'Employees',
            accessor: 'activeEmployees',
            Cell: ({ value }) => (
                <span className="font-medium text-gray-900">{value || 0}</span>
            ),
            sortType: 'number',
        },
        {
            Header: 'Actions',
            accessor: 'id',
            disableSortBy: true,
            Cell: ({ row }) => {
                const isExpanded = expandedId === row.original.id;
                return (
                    <button
                        type="button"
                        onClick={() => setExpandedId(isExpanded ? null : row.original.id)}
                        aria-expanded={isExpanded}
                        className="text-primary hover:text-primary-dark text-sm font-medium inline-flex items-center gap-1 transition-colors"
                    >
                        {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                        ) : (
                            <ChevronDown className="w-4 h-4" />
                        )}
                        Client Summary
                    </button>
                );
            },
        },
    ], [expandedId]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        rows,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: filteredData,
            initialState: { pageIndex: 0, pageSize: 10 },
            autoResetPage: true,
        },
        useSortBy,
        usePagination
    );

    const downloadCSV = () => {
        const headers = ["Company (Policy)", "Employees"];
        let csvContent = headers.join(",") + "\n";

        rows.forEach(row => {
            const company = row.original;
            csvContent += `"${company.name || ''}","${company.activeEmployees || 0}"\n`;
        });

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "Clients.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <PageWrapper>
            <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5 overflow-y-auto">
                {/* page header with icon */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary/10 rounded-xl">
                        <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                            Clients
                        </h1>
                        <p className="text-sm text-gray-500">
                            Policies ({companies.length}) · Employees ({totalEmployees})
                        </p>
                    </div>
                </div>

                {/* content */}
                <div className="bg-white rounded-2xl shadow-card p-6">
                    {loadingError ? (
                        <p className="text-center text-gray-500 py-8">
                            An error occurred while retrieving data. Please refresh the page and try again.
                        </p>
                    ) : companies.length === 0 && Object.keys(userInfo).length !== 0 ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Users className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500">No clients found</p>
                        </div>
                    ) : companies.length > 0 ? (
                        <>
                            {/* header */}
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                                    Policy List
                                </h3>
                                <button
                                    onClick={downloadCSV}
                                    disabled={rows.length === 0}
                                    className="h-9 px-4 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Download CSV"
                                >
                                    <Download className="w-4 h-4" />
                                    <span>CSV</span>
                                </button>
                            </div>

                            {/* Search bar */}
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by company or policy..."
                                    className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            <ScrollX>
                                <table {...getTableProps()} className="w-full text-sm">
                                    <thead>
                                        {headerGroups.map(headerGroup => (
                                            <tr {...headerGroup.getHeaderGroupProps()} className="border-b border-gray-100">
                                                {headerGroup.headers.map(column => (
                                                    <th
                                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                                        className={`py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition-colors ${
                                                            column.id === 'activeEmployees' ? 'text-right' : 'text-left'
                                                        }`}
                                                    >
                                                        <span className={`inline-flex items-center gap-1 ${
                                                            column.id === 'activeEmployees' ? 'justify-end' : ''
                                                        }`}>
                                                            {column.render('Header')}
                                                            {column.isSorted && (
                                                                column.isSortedDesc
                                                                    ? <ChevronDown className="w-3 h-3" />
                                                                    : <ChevronUp className="w-3 h-3" />
                                                            )}
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()} className="divide-y divide-gray-50">
                                        {page.length > 0 ? page.map(row => {
                                            prepareRow(row);
                                            const isExpanded = expandedId === row.original.id;
                                            return (
                                                <Fragment key={row.original.id}>
                                                    <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                                                        {row.cells.map(cell => (
                                                            <td
                                                                {...cell.getCellProps()}
                                                                className={`py-3 px-2 ${
                                                                    cell.column.id === 'activeEmployees' ? 'text-right' : ''
                                                                }`}
                                                            >
                                                                {cell.render('Cell')}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={columns.length} className="p-0">
                                                                <ClientSummaryPanel
                                                                    summaryUrl={`${apiUrl}/advisors/policies/${row.original.id}/summary`}
                                                                    expensesUrlFor={(limitId) =>
                                                                        `${apiUrl}/advisors/policies/${row.original.id}/limits/${limitId}/expenses.pdf`
                                                                    }
                                                                    token={currentUser.token}
                                                                    onCollapse={() => setExpandedId(null)}
                                                                />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan={columns.length} className="py-8 text-center text-sm text-gray-400">
                                                    No results found for <strong>"{searchQuery}"</strong>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </ScrollX>

                            {/* pagination */}
                            {filteredData.length > 10 && (
                                <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => gotoPage(0)}
                                            disabled={!canPreviousPage}
                                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="First page"
                                        >
                                            <ChevronsLeft className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={previousPage}
                                            disabled={!canPreviousPage}
                                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Previous page"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={nextPage}
                                            disabled={!canNextPage}
                                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Next page"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => gotoPage(pageOptions.length - 1)}
                                            disabled={!canNextPage}
                                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Last page"
                                        >
                                            <ChevronsRight className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                        <span>
                                            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                                        </span>
                                        <span className="flex items-center gap-2">
                                            Go to:
                                            <input
                                                type="number"
                                                min={1}
                                                max={pageOptions.length}
                                                defaultValue={pageIndex + 1}
                                                onChange={(e) => {
                                                    const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                                    gotoPage(pageNumber);
                                                }}
                                                className="w-14 px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                            />
                                        </span>
                                    </div>

                                    <select
                                        value={pageSize}
                                        onChange={(e) => setPageSize(Number(e.target.value))}
                                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                    >
                                        {[10, 20, 30, 40].map((size) => (
                                            <option key={size} value={size}>
                                                Show {size}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        </PageWrapper>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompaniesPage);